import { validateReturnTo, isExistingBookingNavigable } from '@/components/Prescriptions/criteriaNavigation'
import { addSequentialParams } from '@/lib/sequentialFlow'

export const MEDICATION_CHECK_IN_PATH = '/account/medication-check-in'
export const MEDICATION_CHECK_IN_LABS_PATH = '/account/medication-check-in/labs'
export const MEDICATION_CHECK_IN_LABS_UPLOAD_PATH = '/account/medication-check-in/labs/upload'
export const MEDICATION_CHECK_IN_PROGRESS_PATH = '/account/medication-check-in/progress'
export const MEDICATION_CHECK_IN_COMPLETE_PATH = '/account/medication-check-in/complete'
export const MEDICATION_CHECK_IN_RETURN_QUERY = 'return_to'
export const MEDICATION_CHECK_IN_ENTRY_RETURN_SESSION_KEY = 'medication_check_in_entry_return'
export const MEDICATION_CHECK_IN_LAB_ORDER_ID_SESSION_KEY = 'medication_check_in_lab_order_id'

function isBrowser(): boolean {
    return typeof window !== 'undefined'
}

export function captureMedicationCheckInEntryReturn(searchParams: URLSearchParams): void {
    if (!isBrowser()) return

    const entryReturn = resolveMedicationCheckInReturn(searchParams)
    if (!entryReturn || isMedicationCheckInReturnTo(entryReturn)) {
        return
    }

    sessionStorage.setItem(MEDICATION_CHECK_IN_ENTRY_RETURN_SESSION_KEY, entryReturn)
}

export function readMedicationCheckInEntryReturn(): string {
    if (!isBrowser()) return '/account'

    try {
        return validateReturnTo(sessionStorage.getItem(MEDICATION_CHECK_IN_ENTRY_RETURN_SESSION_KEY)) ?? '/account'
    } catch {
        return '/account'
    }
}

export function clearMedicationCheckInEntryReturn(): void {
    if (!isBrowser()) return

    try {
        sessionStorage.removeItem(MEDICATION_CHECK_IN_ENTRY_RETURN_SESSION_KEY)
    } catch {
        // sessionStorage may be unavailable in private mode.
    }
}

export function isMedicationCheckInReturnTo(path: string): boolean {
    if (path === MEDICATION_CHECK_IN_PATH || path.startsWith(`${MEDICATION_CHECK_IN_PATH}/`)) {
        return true
    }

    return /^\/prescriptions\/[A-Za-z0-9_-]+\/check-in(\/|$)/.test(path)
}


export function resolveMedicationCheckInReturn(searchParams: URLSearchParams): string | null {
    return validateReturnTo(searchParams.get(MEDICATION_CHECK_IN_RETURN_QUERY))
}

/** Lab booking started from the medication renewal check-in bundled-labs step (account hub or prescription-scoped). */
export function isMedicationCheckInLabCheckoutFlow(searchParams: URLSearchParams): boolean {
    const returnTo = resolveMedicationCheckInReturn(searchParams)
    return returnTo != null && isMedicationCheckInReturnTo(returnTo)
}

export function appendMedicationCheckInReturnParams(
    baseUrl: string,
    searchParams: URLSearchParams,
): string {
    const withSequential = addSequentialParams(baseUrl, searchParams)
    if (withSequential.includes(`${MEDICATION_CHECK_IN_RETURN_QUERY}=`)) {
        return withSequential
    }

    const returnTo = validateReturnTo(searchParams.get(MEDICATION_CHECK_IN_RETURN_QUERY))
    if (!returnTo) {
        return withSequential
    }

    const separator = withSequential.includes('?') ? '&' : '?'
    return `${withSequential}${separator}${MEDICATION_CHECK_IN_RETURN_QUERY}=${encodeURIComponent(returnTo)}`
}

export function buildMedicationCheckInOrdersLabsUrl(labOrderId: string): string {
    return `/orders/${encodeURIComponent(labOrderId)}/labs?${MEDICATION_CHECK_IN_RETURN_QUERY}=${encodeURIComponent(MEDICATION_CHECK_IN_PATH)}`
}

export function parseMedicationCheckInProgressLabOrderId(value: string | null | undefined): string | null {
    const trimmed = value?.trim()
    if (!trimmed) return null
    return isExistingBookingNavigable({ type: 'lab', id: trimmed }) ? trimmed : null
}

export function buildMedicationCheckInProgressPath(
    from: 'lab' | 'questionnaire' | 'lab_upload',
    options?: { labOrderId?: string | null },
): string {
    const params = new URLSearchParams({ from })
    const labOrderId = parseMedicationCheckInProgressLabOrderId(options?.labOrderId)
    if (labOrderId) {
        params.set('lab_order_id', labOrderId)
    }
    return `${MEDICATION_CHECK_IN_PROGRESS_PATH}?${params.toString()}`
}
